import { tenantNow, toTenantDate, parseTenantDateTime, toLocalWallClock } from '@utils/timeZone';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@components/ui/dialog';
import { Form, FormField, FormMessage } from '@components/ui/form';
import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { Checkbox } from '@components/ui/checkbox';
import { Input } from '@components/ui/form/input';
import { getHours, getMinutes, parseISO, set } from 'date-fns';
import { formatDate, formatTime } from '@utils/i18n';
import { JobTaskPill } from '@components/job/workProgress/task/pill';
import { ExternalScheduleDialogProps } from './types';
import { ExternalScheduleActions } from './actions';
import { FormRequired } from '@components/ui/form/required';
import { AlertBanner } from '@components/ui/alertBanner';
import { useDropdownsContext } from '@contexts/dropdowns';
import { defaultValues } from '@forms/schedules/external/constants';
import { ExternalScheduleFormValues } from '@forms/schedules/external/types';
import { useErrors } from '@hooks/errors';
import { getHiddenErrorFields, getRelatedTasks } from './helpers';
import { JobTask } from '@root/globalTypes';
import { hasDirtyValues } from '@utils/formHelpers';
import { ExternalScheduleMobileLayout } from './mobile';
import { ExternalScheduleWhenInputs } from './inputs/when';
import { ExternalDateBar } from './mobile/dateBar';
import { getRelatedStartMinutes, getRelatedStarts, withTime } from './inputs/when/helpers';
import { getJobSubtitle } from '../internal/helpers';
import { Reveal } from '@components/ui/reveal';

export const ExternalScheduleDialog = ({
  form,
  onSubmit,
  type,
  open,
  setOpen,
  completed,
  locked,
  children,
  jobId,
  name,
  taskId,
  schedule,
  importantTasks,
  note,
  concreteDirectId,
  extraComment,
  readOnly,
  scheduleType,
  phoneLayout,
  nested,
  takenDays,
  whenDisabled,
  concrete,
  completeAfterSave,
}: ExternalScheduleDialogProps) => {
  const {
    companies: { options, disabled: companiesDisabled },
  } = useDropdownsContext();

  const { t } = useTranslation('app');
  const handleErrors = useErrors();
  const initializedFor = useRef<string | null>(null);

  const { setValue, watch, reset } = form;
  const unsavedMaterialChanges = hasDirtyValues(form.formState.dirtyFields.materialOptions);

  const externalContactId = watch('externalContactId');
  const noPhoneNumber = ![
    ...options.companies.flatMap((el) => el.contacts),
    ...options.workflowContacts,
  ].find((el) => el.id === externalContactId)?.phone;

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      reset(defaultValues);
      return;
    }
    const key = `${schedule?.task?.id}-${schedule?.id}`;
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    if (note?.content) setValue('note', note.content);
    if (extraComment) setValue('extraComment', extraComment);
    if (schedule?.status === 'approved') setValue('confirmed', true);
    else setValue('confirmed', false);
    if (phoneLayout && !schedule?.startsAt) return;
    if (phoneLayout && schedule?.startsAt && !schedule.startsAt.includes('T')) {
      const day = toLocalWallClock(parseTenantDateTime(schedule.startsAt));
      setValue('date', day);
      const relatedStart = getRelatedStartMinutes(day, getRelatedStarts(importantTasks));
      if (relatedStart !== undefined) setValue('time', withTime(day, relatedStart));
      return;
    }
    const date = toLocalWallClock(
      schedule?.startsAt ? parseTenantDateTime(schedule.startsAt) : tenantNow(),
    );
    setValue('time', date);
    setValue('date', date);
  }, [setValue, note, extraComment, schedule, open, reset, phoneLayout, importantTasks]);

  const onInvalid = (errors: FieldErrors<ExternalScheduleFormValues>) => {
    const hiddenFields = getHiddenErrorFields(errors, type);
    if (!hiddenFields.length) return;
    Sentry.captureMessage('External schedule form blocked by hidden validation errors', {
      extra: { type, fields: hiddenFields, taskId },
    });
    handleErrors(`form_hidden_errors ${hiddenFields.join(', ')}`);
  };

  const sortedTasks = importantTasks?.sort(
    (a, b) =>
      parseISO(a.schedule?.startsAt || '').getTime() -
      parseISO(b.schedule?.startsAt || '').getTime(),
  );

  const renderPill = (task: JobTask) => (
    <JobTaskPill key={`important-task-${task.id}`} status={task.status}>
      {t('schedules.scheduledAt', {
        stage: task.name,
        action: t(`pages.job.info.status.${task.status}`),
        date: task?.schedule?.startsAt ? formatDate(toTenantDate(task.schedule.startsAt)) : '',
        time: task?.schedule?.startsAt ? formatTime(toTenantDate(task.schedule.startsAt)) : '',
      })}
    </JobTaskPill>
  );

  const pills = sortedTasks?.map(renderPill);

  const noPhoneBanner = externalContactId && !companiesDisabled && noPhoneNumber && (
    <AlertBanner type="tooltip">{t('schedules.noPhoneNumber')}</AlertBanner>
  );

  const mainContactBanner = form.formState.errors.taskId && (
    <div className="flex items-center gap-3 bg-danger-400 px-11 py-3 text-sm font-extrabold text-white">
      <span>
        <SvgConsumer className="text-xl" id="error" />
      </span>
      <span className="flex flex-col">
        <span>{t('schedules.mainContact')}</span>
        <span>{t('schedules.addMainContact')}</span>
      </span>
    </div>
  );

  const completedBanner = completed && (
    <div className="flex items-center gap-3 bg-success-400 px-11 py-3 text-sm font-extrabold text-white">
      <span>
        <SvgConsumer className="text-xl" id="tooltip" />
      </span>
      {t('schedules.completedTooltip')}
    </div>
  );

  const banners = (
    <>
      {mainContactBanner}
      {completedBanner}
    </>
  );

  const details = (
    <>
      <FormField
        control={form.control}
        name="extraComment"
        render={({ field }) => (
          <FormItemWrapper
            label={
              <div className="flex items-center gap-2">
                {t('forms.extraComment.label')}
                <AppTooltip side="top" context={t('forms.extraComment.tooltip')}>
                  <span>
                    <SvgConsumer className="text-m text-neutral-500" id="tooltip" />
                  </span>
                </AppTooltip>
              </div>
            }>
            <Input disabled={completed || readOnly} {...field} />
          </FormItemWrapper>
        )}
      />
      <FormField
        control={form.control}
        name="note"
        render={({ field }) => (
          <FormItemWrapper label={t('forms.note.label')}>
            <Input disabled={completed || readOnly} {...field} />
          </FormItemWrapper>
        )}
      />
    </>
  );

  const confirmedTip = [
    t('schedules.confirmedTooltip'),
    type.includes('inspection') && t('schedules.assigneeTooltip'),
  ]
    .filter(Boolean)
    .join(' ');

  const confirmedField = (
    <FormField
      control={form.control}
      name="confirmed"
      render={({ field: { value, onChange, ...rest } }) => (
        <FormItemWrapper>
          <Checkbox
            {...rest}
            disabled={completed || readOnly}
            checked={value}
            onCheckedChange={onChange}>
            <div className="flex items-center gap-2">
              {t('forms.confirmed.label')}
              <AppTooltip side="top" context={<p>{confirmedTip}</p>}>
                <span>
                  <SvgConsumer className="text-m text-neutral-500" id="tooltip" />
                </span>
              </AppTooltip>
            </div>
          </Checkbox>
        </FormItemWrapper>
      )}
    />
  );

  const actionsProps = {
    setOpen,
    locked,
    completed,
    jobId,
    taskId,
    scheduleId: schedule?.id,
    concreteDirectId,
    readOnly,
    scheduleType,
    unsavedChanges: unsavedMaterialChanges,
  };

  const withDetails = type !== 'concreteDirectOrder';

  if (phoneLayout) {
    const formDate = watch('date');
    const formTime = watch('time');
    const selfStart =
      formDate && formTime
        ? set(formDate, { hours: getHours(formTime), minutes: getMinutes(formTime) })
        : undefined;
    const relatedTasks = getRelatedTasks(sortedTasks, taskId);
    const related = [
      ...relatedTasks.map((task) => ({
        start: toLocalWallClock(toTenantDate(task.schedule?.startsAt || '')).getTime(),
        node: renderPill(task),
      })),
      ...(selfStart
        ? [
            {
              start: selfStart.getTime(),
              node: (
                <JobTaskPill
                  key="self-task"
                  status={completed ? 'completed' : 'scheduled'}
                  className="bg-primary-500 text-white">
                  {t('schedules.scheduledAt', {
                    stage: name,
                    action: t(`pages.job.info.status.${completed ? 'completed' : 'scheduled'}`),
                    date: formatDate(selfStart),
                    time: formatTime(selfStart),
                  })}
                </JobTaskPill>
              ),
            },
          ]
        : []),
    ]
      .sort((a, b) => a.start - b.start)
      .map((el) => el.node);

    return (
      <ExternalScheduleMobileLayout
        open={open}
        setOpen={setOpen}
        title={name}
        subtitle={getJobSubtitle(schedule)}
        form={form}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(onSubmit, onInvalid)}
        contactRequired={type === 'generic'}
        material={concrete}
        completeAfterSave={completeAfterSave}
        dateBar={
          <ExternalDateBar
            date={formDate}
            time={formTime}
            confirmed={watch('confirmed')}
            related={related}
          />
        }
        when={
          <ExternalScheduleWhenInputs
            disabled={whenDisabled}
            takenDays={takenDays}
            importantTasks={relatedTasks}
            currentDay={
              schedule?.id && schedule.startsAt ? toTenantDate(schedule.startsAt) : undefined
            }
          />
        }
        banners={
          <>
            {mainContactBanner}
            {noPhoneBanner && <div className="bg-white px-4 pt-3">{noPhoneBanner}</div>}
          </>
        }
        withDetails={withDetails}
        detailsDisabled={completed || readOnly}
        confirmedTip={confirmedTip}
        nested={nested}
        actions={actionsProps}>
        {children}
      </ExternalScheduleMobileLayout>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader className="gap-1">
          <DialogTitle>{name}</DialogTitle>
          <Reveal
            show={Boolean(pills?.length)}
            className="!-mt-1"
            contentClassName="flex flex-col gap-2.5 pt-2.5">
            {pills}
          </Reveal>
          {noPhoneBanner}
        </DialogHeader>

        {banners}

        <Form {...form}>
          <form
            className="flex flex-col gap-6"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
            <div className="space-y-4">
              {children}
              {withDetails && (
                <>
                  {details}
                  {confirmedField}
                </>
              )}
              <FormRequired />
              <FormField name="root" render={() => <FormMessage />} />
            </div>
            <ExternalScheduleActions {...actionsProps} />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
